import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse, message, Spin } from 'antd';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import config from '../../../services/config';
import { useAuth } from '../../../contexts/AuthContext';
import { createCheckout, getSubscription } from '../../../services/billingApi';
import { trackEvent } from '../../../utils/analytics';
import PricingCards from '../PricingCards';
import './PricingPage.css';
import {
  PRICING_META_DESCRIPTION,
  PRICING_SOCIAL_DESCRIPTION,
  PRICING_SHORT_DESCRIPTION,
} from '../../../content/pricingFigures';
import { PRICING_FAQ } from '../../../content/pricingFaq';

const { Panel } = Collapse;

// The FAQ lives in content/pricingFaq.js (Oct 10 2026): the page and its .md alternate read the same list.
const FAQ_ITEMS = PRICING_FAQ;

const PricingPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const baseUrl = config.API_BASE_URL;
        const response = await axios.get(`${baseUrl}/cellcog/billing/plans`);
        setPlans(response.data.plans || []);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [hasPositiveBalance, setHasPositiveBalance] = useState(false);

  // Fetch subscription wallet balance for logged-in users (for "Preserve & Subscribe" button)
  useEffect(() => {
    if (!currentUser) return;
    const fetchBalance = async () => {
      try {
        const response = await getSubscription();
        const wallets = response.data?.wallets || [];
        // Check if any subscription wallet has positive balance
        const hasCredits = wallets.some(
          (w) => (w.wallet_type === 'PERMANENT' || w.wallet_type === 'ORG_SUBSCRIPTION') && w.balance > 0
        );
        setHasPositiveBalance(hasCredits);
      } catch (err) {
        // Non-critical — default to "Subscribe" if fetch fails
      }
    };
    fetchBalance();
  }, [currentUser]);

  const handlePlanSelect = async (tier, interval) => {
    // Instrumentation (Aug 25 2026, Nitish live session): /pricing's own plan
    // buttons were the site's last untracked money-page CTAs. Scoped HERE (the
    // /pricing page callback), NOT inside the shared PricingCards component, so
    // in-app billing surfaces (Subscribe overlays etc.) stay untracked product
    // UI. Event name reuses the sitewide cta_click convention.
    trackEvent('cta_click', {
      cta_name: 'pricing_plan_select',
      page: 'pricing',
      location: 'plan_cards',
      plan_tier: tier,
      billing_interval: interval,
      logged_in: !!currentUser,
    });
    if (!currentUser) {
      navigate(`/signup?plan=${tier}&interval=${interval}`);
      return;
    }

    // Logged-in user — create checkout session and redirect to Stripe
    setCheckoutLoading(true);
    try {
      const response = await createCheckout(tier, interval, null, 'pricing_page'); // Oct 30 2026: attribution
      const checkoutUrl = response.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        message.error('Failed to create checkout session');
        setCheckoutLoading(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      message.error(err.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="pricing-page">
      <Helmet>
        <title>CellCog Pricing — Plans Starting at $8/month</title>
        <meta name="description" content={PRICING_META_DESCRIPTION} />
        <link rel="canonical" href="https://cellcog.ai/pricing" />
        <link rel="alternate" type="text/markdown" href="https://cellcog.ai/pricing/index.md" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="CellCog Pricing — Pay for the work, not the hire" />
        <meta property="og:description" content={PRICING_SOCIAL_DESCRIPTION} />
        <meta property="og:url" content="https://cellcog.ai/pricing" />
        <meta property="og:image" content="https://cellcog.ai/assets/pricing-og.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CellCog Pricing — Pay for the work, not the hire" />
        <meta name="twitter:description" content={PRICING_SHORT_DESCRIPTION} />
        <meta name="twitter:image" content="https://cellcog.ai/assets/pricing-og.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "CellCog Pricing",
          "description": "CellCog subscription plans and pricing",
          "url": "https://cellcog.ai/pricing",
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "8",
            "highPrice": "1000",
            "priceCurrency": "USD",
            "offerCount": "8"
          }
        })}</script>
        {/* FAQPage schema — built from the same FAQ_ITEMS rendered below, so the
            structured data always matches the visible content exactly. High-value
            for AI answer engines, which quote FAQ Q&A directly. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": { "@type": "Answer", "text": item.answer },
          })),
        })}</script>
      </Helmet>

      {/* Hero Section */}
      <div className="pricing-hero">
        <h1 className="pricing-hero-title">Simple, transparent pricing</h1>
        <p className="pricing-hero-subtitle">Start in minutes. Scale as you grow. No hidden fees.</p>
      </div>

      {/* Plan Cards */}
      <div className="pricing-plans-section">
        {loading ? (
          <div className="pricing-loading">
            <Spin size="large" />
          </div>
        ) : (
          <PricingCards
            plans={plans}
            onSelect={handlePlanSelect}
            loading={checkoutLoading}
            showEnterprise={false}
            isPublicMode={true}
            isPublicModeLoggedIn={!!currentUser}
            hasPositiveBalance={hasPositiveBalance}
          />
        )}
      </div>

      {/* Credits Explainer */}
      <div className="pricing-credits-section">
        <h2 className="pricing-section-title">How credits work</h2>
        <p className="pricing-section-subtitle">
          Every AI operation uses credits. More complex tasks use more credits.
        </p>
        <div className="credits-examples">
          <div className="credit-example">
            <span className="credit-example-icon">💬</span>
            <div className="credit-example-content">
              <h4>Chat & Research</h4>
              <p>Quick questions, deep research reports, data analysis</p>
            </div>
          </div>
          <div className="credit-example">
            <span className="credit-example-icon">🎬</span>
            <div className="credit-example-content">
              <h4>Video & Audio</h4>
              <p>AI videos, voiceovers, music, podcasts</p>
            </div>
          </div>
          <div className="credit-example">
            <span className="credit-example-icon">📄</span>
            <div className="credit-example-content">
              <h4>Documents & Apps</h4>
              <p>PDFs, presentations, spreadsheets, interactive web apps</p>
            </div>
          </div>
        </div>
        <p className="credits-topup-note">
          Need more? Purchase additional credits anytime at 90 credits per $1.
        </p>
      </div>

      {/* FAQ Section */}
      <div className="pricing-faq-section">
        <h2 className="pricing-section-title">Frequently asked questions</h2>
        <div className="pricing-faq-container">
          <Collapse
            bordered={false}
            expandIconPosition="end"
            className="pricing-faq-collapse"
          >
            {FAQ_ITEMS.map((item, index) => (
              <Panel
                header={item.question}
                key={index}
                className="pricing-faq-panel"
              >
                <p className="pricing-faq-answer">{item.answer}</p>
              </Panel>
            ))}
          </Collapse>
        </div>
      </div>

      {/* Enterprise Section */}
      <div className="pricing-enterprise-section">
        <h2 className="pricing-section-title">Need a custom plan?</h2>
        <p className="pricing-section-subtitle">
          For organizations needing unlimited credits, dedicated support, and custom billing terms.
        </p>
        <a
          href="/contact"
          className="pricing-enterprise-cta"
          onClick={() => trackEvent('cta_click', { cta_name: 'contact_sales', page: 'pricing', location: 'enterprise' })}
        >
          Contact Sales
        </a>
        <p className="pricing-enterprise-email">Replies within 1 business day</p>
      </div>
    </div>
  );
};

export default PricingPage;
